import { useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListSubheader,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { mapFieldErrors } from '@/api/fieldErrors';
import type { OfferView } from '@/api/generated';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { ApiErrorState } from '@/components/ApiErrorState';
import { SubmitButton } from '@/components/SubmitButton';
import { useActionError, type ActionError } from '@/components/useActionError';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useFreshOnOpen } from '@/components/useFreshOnOpen';
import { useToast } from '@/components/useToast';
import { useLocale } from '@/i18n/useLocale';
import { useTenantApi } from '@/tenant/useTenant';
import { offerLabel, offerPeriodLabel } from './offerLabel';
import { changeOfferErrorMessage, isOfferClosed } from './subscriberErrors';

export interface ChangeOfferDialogProps {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  subscriptionId: number;
  /** Subscriber's current billing interval — drives the period-end note. */
  currentInterval?: OfferView['interval'] | undefined;
  offers: OfferView[];
  /** The offers request failed. Shown in the dialog; the list stays up. */
  loadError?: unknown;
  onRetryLoad?: () => void;
  /** Reloads the offers after a `409 OFFER_NOT_ACTIVE` (a stale list) — the dialog stays open. */
  onRefreshOffers?: (() => void) | undefined;
}

const FIELDS = ['retailOfferId'] as const;

export function ChangeOfferDialog(props: ChangeOfferDialogProps) {
  // A fresh form on every open (TMMRSL-0071).
  return <ChangeOfferForm key={useFreshOnOpen(props.open)} {...props} />;
}

function ChangeOfferForm({
  open,
  onClose,
  onDone,
  subscriptionId,
  currentInterval,
  offers,
  loadError = null,
  onRetryLoad,
  onRefreshOffers,
}: ChangeOfferDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const showToast = useToast();
  const api = useTenantApi();
  const describe = useActionError(changeOfferErrorMessage);
  const offerRef = useRef<HTMLInputElement>(null);
  const active = offers.filter((offer) => offer.selectable === true);
  const monthly = active.filter((offer) => offer.interval !== 'YEAR');
  const annual = active.filter((offer) => offer.interval === 'YEAR');
  // The first active offer as the dialog opens (it mounts fresh on each open). A refresh can drop
  // the picked offer (it closed): nothing is picked until the person chooses.
  const [picked, setPicked] = useState<number | ''>(active[0]?.id ?? '');
  const offerId = active.some((offer) => offer.id === picked) ? picked : '';
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const fields = useFieldErrors(FIELDS, { retailOfferId: offerRef }, submitting);

  const chosen = active.find((offer) => offer.id === offerId);
  const showPeriodEndNote =
    chosen?.interval !== undefined &&
    currentInterval !== undefined &&
    chosen.interval !== currentInterval;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!chosen || offerId === '') {
      setError(null);
      fields.fail({ retailOfferId: t('validation.chooseOffer') });
      return;
    }
    setSubmitting(true);
    setError(null);
    fields.reset();
    try {
      await api.changeOffer(subscriptionId, { retailOfferId: offerId });
      showToast(t('subscribers.toast.offerChanged'));
      onDone();
      onClose();
    } catch (err) {
      const action = describe(err);
      setError(action);
      fields.failMarked(mapFieldErrors(action.fieldKeys, { retailOfferId: 'retailOfferId' }), {
        retailOfferId: t('errors.fieldInvalid'),
      });
      if (isOfferClosed(err)) onRefreshOffers?.();
    } finally {
      setSubmitting(false);
    }
  }

  function renderOfferItem(offer: OfferView) {
    return (
      <MenuItem key={offer.id} value={offer.id}>
        {offerLabel(
          offer,
          offer.currency,
          locale,
          offerPeriodLabel(offer.interval, t('dashboard.perMonth'), t('dashboard.perYear')),
          t('subscribers.unavailable'),
        )}
      </MenuItem>
    );
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose}>
      <Box component="form" noValidate onSubmit={(e) => void handleSubmit(e)}>
        <DialogTitle>{t('subscribers.changeOffer.title')}</DialogTitle>
        <DialogContent>
          {loadError != null && onRetryLoad ? (
            <ApiErrorState error={loadError} onRetry={onRetryLoad} />
          ) : null}
          <ActionErrorAlert error={error} sx={{ mb: 2 }} />
          {active.length === 0 ? (
            // FEED-002: say why nothing can be chosen, and what to do next.
            <Alert severity="info">{t('subscribers.changeOffer.noneAvailable')}</Alert>
          ) : (
            <TextField
              select
              required
              fullWidth
              margin="dense"
              label={t('subscribers.col.offer')}
              value={offerId}
              onChange={(e) => {
                setPicked(Number(e.target.value));
                fields.clear('retailOfferId');
              }}
              id="change-offer-select"
              inputRef={offerRef}
              error={fields.errors.retailOfferId !== undefined}
              helperText={fields.errors.retailOfferId}
              disabled={submitting}
            >
              {monthly.length > 0 ? (
                <ListSubheader>{t('pricing.interval.MONTH')}</ListSubheader>
              ) : null}
              {monthly.map(renderOfferItem)}
              {annual.length > 0 ? (
                <ListSubheader>{t('pricing.interval.YEAR')}</ListSubheader>
              ) : null}
              {annual.map(renderOfferItem)}
            </TextField>
          )}
          {showPeriodEndNote ? (
            <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
              {t('subscribers.changeOffer.periodEndNote')}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          {/* Disabled only with no offer to choose, and the dialog says so above (TMM-B5). */}
          <SubmitButton type="submit" loading={submitting} disabled={active.length === 0}>
            {t('subscribers.actions.changeOffer')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
